/**
 * LibraryPicker — modal for picking a paper from the existing library.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { apiJson, buildApiUrl } from '../api';

const PREVIEW_BREAKPOINT = 980;
const ROLE_LABELS = { question: 'Question', markscheme: 'Mark scheme', solutions: 'Solutions', insert: 'Insert', other: 'Other' };
const ROLE_ORDER = ['question', 'markscheme', 'solutions', 'insert', 'other'];

export default function LibraryPicker({ onPick, onClose, existingPaperIds = [] }) {
  const [levels, setLevels] = useState([]);
  const [level, setLevel] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState('');
  const [boards, setBoards] = useState([]);
  const [board, setBoard] = useState('');
  const [papers, setPapers] = useState([]);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addedIds, setAddedIds] = useState(() => new Set(existingPaperIds));
  const [pendingId, setPendingId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [previewPaper, setPreviewPaper] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewRole, setPreviewRole] = useState('question');
  const [showPreviewPane, setShowPreviewPane] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= PREVIEW_BREAKPOINT : true
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => {
      if (window.innerWidth >= PREVIEW_BREAKPOINT) setShowPreviewPane(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load preview details when selection changes
  useEffect(() => {
    if (!previewId) { setPreviewPaper(null); setPreviewError(''); return undefined; }
    let cancelled = false;
    setPreviewLoading(true); setPreviewError('');
    apiJson(`/api/papers/${previewId}`)
      .then((data) => {
        if (cancelled) return;
        const p = data.paper || data;
        setPreviewPaper(p);
        const firstRole = ROLE_ORDER.find((r) => p?.files?.[r]) || 'question';
        setPreviewRole(firstRole);
      })
      .catch((err) => { if (!cancelled) setPreviewError(err.message || 'Failed to load preview'); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [previewId]);

  useEffect(() => {
    apiJson('/api/levels').then((r) => setLevels(Array.isArray(r) ? r : (r.levels || []))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!level) { setSubjects([]); setSubject(''); return; }
    apiJson(`/api/subjects/${encodeURIComponent(level)}`)
      .then((r) => setSubjects(Array.isArray(r) ? r : (r.subjects || [])))
      .catch(() => setSubjects([]));
    setSubject(''); setBoard(''); setBoards([]); setPapers([]);
  }, [level]);

  useEffect(() => {
    if (!level || !subject) { setBoards([]); setBoard(''); return; }
    apiJson(`/api/boards/${encodeURIComponent(level)}/${encodeURIComponent(subject)}`)
      .then((r) => setBoards(Array.isArray(r) ? r : (r.boards || [])))
      .catch(() => setBoards([]));
    setBoard(''); setPapers([]);
  }, [level, subject]);

  const loadPapers = useCallback(async () => {
    if (!level || !subject) return;
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ level, subject });
      if (board) params.set('board', board);
      const r = await apiJson(`/api/papers?${params.toString()}`);
      setPapers(r.papers || []);
    } catch (err) {
      setError(err.message || 'Failed to load papers'); setPapers([]);
    } finally { setLoading(false); }
  }, [level, subject, board]);

  useEffect(() => { loadPapers(); }, [loadPapers]);

  const categories = Array.from(new Set(papers.map((p) => p.category).filter(Boolean))).sort();

  useEffect(() => {
    if (category && !categories.includes(category)) setCategory('');
  }, [categories, category]);

  const filtered = papers.filter((p) => {
    if (category && p.category !== category) return false;
    if (search && !(p.title || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="cal-modal-backdrop" onClick={onClose} role="presentation">
      <div className="cal-modal cal-modal-wide" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Pick a paper">
        <div className="cal-modal-head">
          <h3>Pick from library{addedIds.size > existingPaperIds.length && <span style={{ marginLeft: 8, fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 500 }}>· {addedIds.size - existingPaperIds.length} added</span>}</h3>
          <button type="button" className="cal-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="cal-picker-body">
          <div className="cal-picker-left">
            <div className="cal-picker-filters">
              <label className="tt-field">
                <span>Level</span>
                <select value={level} onChange={(e) => setLevel(e.target.value)}>
                  <option value="">Choose level</option>
                  {levels.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>
              <label className="tt-field">
                <span>Subject</span>
                <select value={subject} onChange={(e) => setSubject(e.target.value)} disabled={!level}>
                  <option value="">Choose subject</option>
                  {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="tt-field">
                <span>Board</span>
                <select value={board} onChange={(e) => setBoard(e.target.value)} disabled={!subject}>
                  <option value="">All boards</option>
                  {boards.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <label className="tt-field">
                <span>Type</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={!categories.length}>
                  <option value="">All types</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <div className="cal-picker-search">
              <input
                type="search"
                placeholder="Filter by title…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="cal-picker-results">
              {error && <div className="tt-status error">{error}</div>}
              {!level || !subject ? (
                <p className="cal-picker-hint">Pick a level and subject to see papers.</p>
              ) : loading ? (
                <p className="cal-picker-hint">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="cal-picker-hint">No papers found.</p>
              ) : (
                <ul className="cal-picker-list">
                  {filtered.map((p) => {
                    const added = addedIds.has(p.id);
                    const busy = pendingId === p.id;
                    const isPreview = previewId === p.id;
                    return (
                      <li key={p.id}>
                        <div
                          className={`cal-picker-row${isPreview ? ' is-preview' : ''}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => { setPreviewId(p.id); setShowPreviewPane(true); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewId(p.id); setShowPreviewPane(true); } }}
                          aria-pressed={isPreview}
                        >
                          <div>
                            <div className="cal-picker-title">
                              <span className="cal-picker-board" data-board={p.board}>{p.board}</span>
                              {p.title}
                              {p.groupLabel ? <span style={{ marginLeft: 6, fontWeight: 500, color: 'var(--muted)' }}>· {p.groupLabel}</span> : null}
                            </div>
                            <div className="cal-picker-meta">{p.level} · {p.subject} · {p.category}</div>
                          </div>
                          <button
                            type="button"
                            className={added ? 'secondary-btn cal-picker-add' : 'primary-btn cal-picker-add'}
                            disabled={added || busy}
                            onClick={async (e) => {
                              e.stopPropagation();
                              setPendingId(p.id);
                              try {
                                await onPick({ kind: 'paper', paperId: p.id, label: p.title });
                                setAddedIds((prev) => { const n = new Set(prev); n.add(p.id); return n; });
                              } catch (err) {
                                setError(err.message || 'Failed to add paper');
                              } finally { setPendingId(null); }
                            }}
                          >{added ? '✓ Added' : busy ? 'Adding…' : '+ Add'}</button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <PreviewPane
            visible={showPreviewPane}
            previewId={previewId}
            paper={previewPaper}
            loading={previewLoading}
            error={previewError}
            role={previewRole}
            setRole={setPreviewRole}
            added={previewId ? addedIds.has(previewId) : false}
            busy={previewId !== null && pendingId === previewId}
            onAdd={async () => {
              if (!previewPaper) return;
              setPendingId(previewPaper.id);
              try {
                await onPick({ kind: 'paper', paperId: previewPaper.id, label: previewPaper.title });
                setAddedIds((prev) => { const n = new Set(prev); n.add(previewPaper.id); return n; });
              } catch (err) {
                setPreviewError(err.message || 'Failed to add paper');
              } finally { setPendingId(null); }
            }}
            onClosePane={() => setShowPreviewPane(false)}
          />
        </div>
        <div className="cal-edit-actions" style={{ padding: '0.75rem 1.1rem', borderTop: '1px solid var(--chip-border)' }}>
          <button type="button" className="primary-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

function PreviewPane({ visible, previewId, paper, loading, error, role, setRole, added, busy, onAdd, onClosePane }) {
  if (!visible) return null;
  // Use a same-origin path for the iframe so X-Frame-Options: SAMEORIGIN doesn't
  // block the preview when the page is served from a different host than
  // REACT_APP_API_URL. Every vhost proxies /files/ to the backend.
  const rawUrl = paper?.files?.[role]?.url || null;
  const fileUrl = rawUrl || null; // for "Open in new tab"
  const sameOriginUrl = rawUrl ? rawUrl : null;
  const availableRoles = paper ? ROLE_ORDER.filter((r) => paper.files?.[r]) : [];
  return (
    <aside className="cal-picker-preview" aria-label="Paper preview">
      <div className="cal-picker-preview-head">
        <strong>Preview</strong>
        <button type="button" className="cal-icon-btn" onClick={onClosePane} aria-label="Hide preview">×</button>
      </div>
      {!previewId ? (
        <div className="cal-picker-preview-empty">
          <p>Tap a paper on the left to preview it here.</p>
        </div>
      ) : loading ? (
        <div className="cal-picker-preview-empty"><p>Loading preview…</p></div>
      ) : error ? (
        <div className="cal-picker-preview-empty"><p style={{ color: '#b91c1c' }}>{error}</p></div>
      ) : !paper ? null : (
        <>
          <div className="cal-picker-preview-meta">
            <div className="cal-picker-title" style={{ whiteSpace: 'normal' }}>{paper.title}</div>
            <div className="cal-picker-meta">{paper.board} · {paper.level} · {paper.subject} · {paper.category}</div>
          </div>
          {availableRoles.length > 1 && (
            <div className="cal-picker-preview-tabs" role="tablist">
              {availableRoles.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="tab"
                  aria-selected={role === r}
                  className={`cal-picker-preview-tab${role === r ? ' is-active' : ''}`}
                  onClick={() => setRole(r)}
                >{ROLE_LABELS[r] || r}</button>
              ))}
            </div>
          )}
          <div className="cal-picker-preview-frame">
            {sameOriginUrl ? (
              <iframe
                key={sameOriginUrl}
                title={`${paper.title} — ${ROLE_LABELS[role] || role}`}
                src={`${sameOriginUrl}#toolbar=0&navpanes=0&view=FitH`}
              />
            ) : (
              <div className="cal-picker-preview-empty"><p>No file for this tab.</p></div>
            )}
          </div>
          <div className="cal-picker-preview-actions">
            {fileUrl && (
              <a className="secondary-btn" href={buildApiUrl(fileUrl)} target="_blank" rel="noreferrer">Open in new tab</a>
            )}
            <button
              type="button"
              className={added ? 'secondary-btn' : 'primary-btn'}
              disabled={added || busy}
              onClick={onAdd}
            >{added ? '✓ Added' : busy ? 'Adding…' : '+ Add to calendar'}</button>
          </div>
        </>
      )}
    </aside>
  );
}
